import React, { useState, useEffect, useCallback } from 'react';
import { changeOrderApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Plus, Trash2, CheckCircle2, FileEdit, Hammer, FilePlus } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const emptyLine = () => ({ description: '', kind: 'other', quantity: 1, unit_price: 0 });
const emptyForm = (type = 'change_order') => ({
  type, title: '', tax_rate: 0, notes: '', line_items: [emptyLine()],
});

const lineTotal = (li) => (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0);

export const ProjectChangeOrders = ({ projectId, onChanged }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await changeOrderApi.getForProject(projectId);
      setOrders(res.data || []);
    } catch (e) {
      toast.error('Failed to load change orders');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const openNew = (type) => {
    setEditingId(null);
    setForm(emptyForm(type));
    setDialogOpen(true);
  };

  const openEdit = (co) => {
    setEditingId(co.id);
    setForm({
      type: co.type, title: co.title, tax_rate: co.tax_rate || 0, notes: co.notes || '',
      line_items: (co.line_items || []).map((l) => ({
        description: l.description, kind: l.kind || 'other', quantity: l.quantity, unit_price: l.unit_price,
      })),
    });
    setDialogOpen(true);
  };

  const updateLine = (idx, field, value) => {
    const items = [...form.line_items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, line_items: items });
  };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, emptyLine()] });
  const removeLine = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

  const subtotal = form.line_items.reduce((s, li) => s + lineTotal(li), 0);
  const taxAmount = subtotal * (parseFloat(form.tax_rate) || 0) / 100;
  const total = subtotal + taxAmount;

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Add a title'); return; }
    const payload = {
      type: form.type, title: form.title.trim(),
      tax_rate: parseFloat(form.tax_rate) || 0, notes: form.notes,
      line_items: form.line_items
        .filter((l) => l.description.trim())
        .map((l) => ({ description: l.description.trim(), kind: l.kind,
          quantity: parseFloat(l.quantity) || 0, unit_price: parseFloat(l.unit_price) || 0 })),
    };
    if (payload.line_items.length === 0) { toast.error('Add at least one line item'); return; }
    setSaving(true);
    try {
      if (editingId) await changeOrderApi.update(editingId, payload);
      else await changeOrderApi.create(projectId, payload);
      toast.success(editingId ? 'Change order updated' : 'Change order created');
      setDialogOpen(false);
      await fetchOrders();
      onChanged && onChanged();
    } catch (e) {
      toast.error('Failed to save change order');
    } finally {
      setSaving(false);
    }
  };

  const approve = async (co) => {
    try {
      await changeOrderApi.approve(co.id);
      toast.success(`${co.co_number} approved`);
      await fetchOrders();
      onChanged && onChanged();
    } catch (e) { toast.error('Failed to approve'); }
  };
  const reopen = async (co) => {
    try { await changeOrderApi.reopen(co.id); await fetchOrders(); onChanged && onChanged(); }
    catch (e) { toast.error('Failed to reopen'); }
  };
  const remove = async (co) => {
    if (!window.confirm(`Delete ${co.co_number}?`)) return;
    try { await changeOrderApi.remove(co.id); toast.success('Deleted'); await fetchOrders(); onChanged && onChanged(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  const approvedTotal = orders.filter((o) => o.status === 'approved').reduce((s, o) => s + o.total, 0);

  return (
    <Card className="rounded-sm" data-testid="project-change-orders">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-lg">Change Orders &amp; Extras</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Approved total added to contract: <span className="font-semibold text-foreground">{fmt(approvedTotal)}</span>
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openNew('change_order')} data-testid="add-change-order-btn">
              <FilePlus className="h-4 w-4 mr-1" /> Change Order
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => openNew('time_materials')} data-testid="add-tm-btn">
              <Hammer className="h-4 w-4 mr-1" /> T&amp;M
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-center py-8 text-muted-foreground">Loading…</p>
        ) : orders.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">
            No change orders yet. Add a Change Order (fixed-price scope add) or a T&amp;M extra.
          </p>
        ) : (
          <div className="space-y-3">
            {orders.map((co) => (
              <div key={co.id} className="border rounded-sm p-4" data-testid={`change-order-${co.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{co.co_number}</span>
                      <Badge variant="outline" className="text-xs">
                        {co.type === 'time_materials' ? 'T&M' : 'Change Order'}
                      </Badge>
                      {co.status === 'approved' ? (
                        <Badge className="bg-green-700 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Approved</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Draft</Badge>
                      )}
                    </div>
                    <p className="text-sm mt-1">{co.title}</p>
                    <p className="text-xs text-muted-foreground">{co.line_items?.length || 0} line items</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold">{fmt(co.total)}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  {co.status === 'approved' ? (
                    <Button variant="ghost" size="sm" className="h-8 rounded-sm" onClick={() => reopen(co)} data-testid={`reopen-${co.id}`}>Reopen</Button>
                  ) : (
                    <>
                      <Button variant="outline" size="sm" className="h-8 rounded-sm" onClick={() => approve(co)} data-testid={`approve-${co.id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 rounded-sm" onClick={() => openEdit(co)} data-testid={`edit-${co.id}`}>
                        <FileEdit className="h-4 w-4 mr-1" /> Edit
                      </Button>
                    </>
                  )}
                  <Button variant="ghost" size="sm" className="h-8 rounded-sm text-red-600 hover:text-red-700" onClick={() => remove(co)} data-testid={`delete-${co.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit' : 'New'} {form.type === 'time_materials' ? 'T&M Extra' : 'Change Order'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="w-40">
                <Label className="text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="co-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="change_order">Change Order</SelectItem>
                    <SelectItem value="time_materials">Time &amp; Materials</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-xs">Title</Label>
                <Input className="rounded-sm" placeholder="e.g., Stairway repair before painting"
                  value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="co-title-input" />
              </div>
            </div>

            <div>
              <Label className="text-xs">Line Items</Label>
              <div className="space-y-2 mt-1">
                {form.line_items.map((li, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    {form.type === 'time_materials' && (
                      <Select value={li.kind} onValueChange={(v) => updateLine(idx, 'kind', v)}>
                        <SelectTrigger className="w-28 rounded-sm flex-none"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="labor">Labor</SelectItem>
                          <SelectItem value="material">Material</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Input className="rounded-sm flex-1" placeholder="Description"
                      value={li.description} onChange={(e) => updateLine(idx, 'description', e.target.value)}
                      data-testid={`co-line-desc-${idx}`} />
                    <Input className="rounded-sm w-20" type="number" placeholder="Qty/Hrs"
                      value={li.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} />
                    <Input className="rounded-sm w-24" type="number" placeholder="Rate"
                      value={li.unit_price} onChange={(e) => updateLine(idx, 'unit_price', e.target.value)} />
                    <span className="w-24 text-right text-sm pt-2">{fmt(lineTotal(li))}</span>
                    <Button variant="ghost" size="icon" className="h-9 w-9 flex-none" onClick={() => removeLine(idx)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" className="rounded-sm mt-2" onClick={addLine} data-testid="co-add-line">
                <Plus className="h-4 w-4 mr-1" /> Add line
              </Button>
            </div>

            <div className="flex justify-end gap-6 items-center">
              <div className="w-32">
                <Label className="text-xs">Tax %</Label>
                <Input className="rounded-sm" type="number" value={form.tax_rate}
                  onChange={(e) => setForm({ ...form, tax_rate: e.target.value })} />
              </div>
              <div className="text-right text-sm space-y-1">
                <div>Subtotal: <span className="font-medium">{fmt(subtotal)}</span></div>
                <div>Tax: <span className="font-medium">{fmt(taxAmount)}</span></div>
                <div className="text-base font-bold">Total: {fmt(total)}</div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea className="rounded-sm" rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={handleSave} disabled={saving} data-testid="co-save-btn">
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
